import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Container, Stack, Typography, useTheme } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const features = [
  {
    label: "Compliance Checker",
    description:
      "Paste or photograph a formula. IFRA, EU Annex, and CosIng rules resolve inline against the live regulatory snapshot.",
  },
  {
    label: "Formula Library",
    description:
      "Every run saved with rule hits, allergen disclosures, and market readiness. No spreadsheets.",
  },
  {
    label: "Labeling",
    description:
      "EU and US label drafts generated from the formula — pictograms, PAO, allergen list, responsible person, all wired.",
  },
  {
    label: "Regulatory Intelligence",
    description:
      "Watch Annex updates and IFRA amendments. Impact ripples into product status the moment a rule shifts.",
  },
];

const LandingPage = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        // Subtle brand-tinted glow behind the hero
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "120vw",
          height: "80vh",
          background:
            "radial-gradient(ellipse at center, rgba(27,42,74,0.10) 0%, rgba(27,42,74,0) 60%)",
          pointerEvents: "none",
          zIndex: 0,
        },
      }}
    >
      {/* Top bar — minimal, just the wordmark */}
      <Box
        sx={{
          px: { xs: 3, md: 6 },
          py: { xs: 2.5, md: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 600, letterSpacing: "-0.02em", color: "#1B2A4A" }}
        >
          kōdō
        </Typography>
        <Button
          component={RouterLink}
          to="/app"
          variant="text"
          size="small"
          sx={{ color: "#1B2A4A", fontWeight: 500 }}
        >
          Skip to platform →
        </Button>
      </Box>

      {/* Hero */}
      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
          py: { xs: 6, md: 10 },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "rgba(27,42,74,0.65)",
            letterSpacing: "0.24em",
            fontSize: 12,
            mb: 3,
          }}
        >
          Regulatory intelligence · Cosmetics & fine fragrance
        </Typography>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 44, sm: 60, md: 76 },
            fontWeight: 600,
            letterSpacing: "-0.03em",
            lineHeight: 1.05,
            color: "#1B2A4A",
            mb: 3,
            maxWidth: 880,
          }}
        >
          Ship compliant product,
          <br />
          without the compliance drag.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: 17, md: 19 },
            lineHeight: 1.55,
            color: "rgba(0,0,0,0.68)",
            maxWidth: 640,
            mb: 5,
          }}
        >
          A live rules engine for perfumers and brand ops. Formula in, EU + US
          readiness out. IFRA, Annex II–VI, CosIng, MoCRA — all resolved against
          the current regulatory snapshot.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            component={RouterLink}
            to="/app"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 500,
              borderRadius: "14px",
              background: "#1B2A4A",
              boxShadow: "0 8px 24px rgba(27,42,74,0.20)",
              "&:hover": {
                background: "#2C4468",
                boxShadow: "0 12px 32px rgba(27,42,74,0.28)",
              },
            }}
          >
            Launch demo
          </Button>
          <Button
            component={RouterLink}
            to="/app/compliance-checker"
            variant="outlined"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 500,
              borderRadius: "14px",
              borderColor: "rgba(27,42,74,0.25)",
              color: "#1B2A4A",
              "&:hover": {
                borderColor: "#1B2A4A",
                background: "rgba(27,42,74,0.04)",
              },
            }}
          >
            Try the checker
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{ mt: 3, color: "rgba(0,0,0,0.45)", fontSize: 12 }}
        >
          Hosted demo · seeded portfolio · no signup
        </Typography>
      </Container>

      {/* Feature grid */}
      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, pb: { xs: 8, md: 12 } }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {features.map((feature) => (
            <Box
              key={feature.label}
              sx={{
                p: 3,
                borderRadius: "18px",
                background: theme.glass.surface.background,
                backdropFilter: theme.glass.surface.backdropFilter,
                WebkitBackdropFilter: theme.glass.surface.WebkitBackdropFilter,
                border: theme.glass.surface.border,
                boxShadow: theme.glass.surface.boxShadow,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#1B2A4A",
                  mb: 1,
                  fontSize: 14,
                }}
              >
                {feature.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0,0,0,0.65)",
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}
              >
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          px: { xs: 3, md: 6 },
          py: 3,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.45)" }}>
          © kōdō · Built for cosmetic and fragrance regulatory ops
        </Typography>
        <Button
          component={RouterLink}
          to="/app"
          variant="text"
          size="small"
          sx={{ color: "rgba(27,42,74,0.75)", fontSize: 12 }}
        >
          Launch demo →
        </Button>
      </Box>
    </Box>
  );
};

export default LandingPage;
